import { Component, computed, inject, input, output } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Referral, ReferralStatus } from '@xmedical/core';
import { XmedicalUiModule } from '@xmedical/ui';

@Component({
  selector: 'xm-outgoing-referral-card',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatChipsModule,
    MatTooltipModule,
    XmedicalUiModule
  ],
  providers: [DatePipe],
  templateUrl: './outgoing-referral-card.component.html',
  styleUrl: './outgoing-referral-card.component.scss'
})
export class OutgoingReferralCardComponent {
  private datePipe = inject(DatePipe);

  // Inputs
  referral = input.required<Referral>();

  // Outputs
  cardClick = output<Referral>();

  // Expose ReferralStatus enum to template
  readonly ReferralStatus = ReferralStatus;

  patientNumber = computed(() => {
    return this.referral().referringPatientId;
  });

  patientSnapshot = computed(() => {
    return this.referral()?.referringPatientSnapshot || null;
  });

  patient = computed(() => {
    return this.referral()?.referringPatientSnapshot.patient || null;
  });

  // Computed: Patient display name
  patientName = computed(() => {
    const patient = this.patient();
    if (!patient) {
      return `Patient #${patient.id}`;
    }
    return `${patient.firstName || ''} ${patient.surname || ''}`.trim() ||
      `Patient #${patient.id}`;
  });

  // Computed: Patient age
  patientAge = computed(() => {
    const dob = this.patient()?.dateOfBirth;
    if (!dob) return null;

    const dobDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - dobDate.getFullYear();
    const monthDiff = today.getMonth() - dobDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dobDate.getDate())) {
      age--;
    }
    return `${age}y`;
  });

  // Computed: Patient gender
  patientGender = computed(() => {
    const gender = this.patient()?.gender;
    if (!gender) return null;
    return gender;
  });

  targetDoctorAvatarUrl = computed(() => {
    return this.referral().targetDoctor?.profilePictureUrl || null;
  });

  // Computed: Target doctor name
  targetDoctorName = computed(() => {
    return this.referral().targetDoctor?.name || 'Unknown Doctor';
  });

  // Computed: Medical aid info
  medicalAidInfo = computed(() => {
    return  this.patient()?.medicalAidScheme || null;
  });

  // Computed: Status label
  statusLabel = computed(() => {
    const status = this.referral().status;
    switch (status) {
      case ReferralStatus.Pending:
        return 'Pending';
      case ReferralStatus.Accepted:
        return 'Accepted';
      case ReferralStatus.Rejected:
        return 'Rejected';
      default:
        return 'Unknown';
    }
  });

  // Computed: Status CSS class
  statusClass = computed(() => {
    const status = this.referral().status;
    switch (status) {
      case ReferralStatus.Pending:
        return 'status-pending';
      case ReferralStatus.Accepted:
        return 'status-accepted';
      case ReferralStatus.Rejected:
        return 'status-rejected';
      default:
        return '';
    }
  });

  // Computed: Has allergies (for warning indicator)
  hasAllergies = computed(() => {
    return (this.patientSnapshot()?.allergies?.length ?? 0) > 0;
  });

  formatDate(date: Date | string): string {
    if (!date) return '';
    return this.datePipe.transform(date, 'dd MMM yyyy, HH:mm') || '';
  }

  onCardClick(): void {
    this.cardClick.emit(this.referral());
  }
}
